import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";
import { getFolderIdFromURL } from "../utils/url-parser";
import {
  DEFAULT_DATE_FILTER,
  DOCUMENT_TYPES,
  type DateFilter,
  type DocumentType,
} from "./stories.filters";
import { navigateToFolder } from "./stories.navigation";
import {
  getDateFilterFromStorage,
  getLayoutViewFromStorage,
  getLocationFromStorage,
  getOwnerFromStorage,
  getSortByFromStorage,
  getSortOrderFromStorage,
  getStatusFromStorage,
  getTypesFromStorage,
  saveDateFilterToStorage,
  saveLayoutViewToStorage,
  saveLocationToStorage,
  saveOwnerToStorage,
  saveSortByToStorage,
  saveSortOrderToStorage,
  saveStatusToStorage,
  saveTypesToStorage,
} from "./stories.persistence";

/**
 * Dashboard scope pills. The dashboard list is a preview, so its scope is kept
 * apart from the full Stories page filters and is not persisted.
 */
export type HomeScope = "all" | "created-by-me" | "recent";

/** Identifies a selected row. Folders and documents share one selection. */
export type SelectionKind = "folder" | "story" | "version";

export type SelectedItem = {
  id: string;
  kind: SelectionKind;
};

/**
 * What Cmd+C / Cmd+X put aside, waiting for a Cmd+V.
 *
 * Rows, not ids: by the time it is pasted the user has navigated somewhere
 * else, so the list no longer holds the things being pasted. The rows carry
 * what both paths need — a story to duplicate from, a folder to move.
 *
 * Deliberately not persisted. A clipboard that survived a reload would offer to
 * paste rows that may have been renamed, moved or deleted since.
 */
export type ListClipboard = {
  mode: "copy" | "cut";
  items: ClipboardRow[];
};

/** What the clipboard needs from a list row, without importing the list. */
export type ClipboardRow =
  | { kind: "story"; id: string; story: unknown }
  | { kind: "folder"; id: string; folder: { name: string } };

type StoriesState = {
  type: string;
  status: string[];
  owner: string;
  search: string;
  sortBy: string;
  sortOrder: string;
  folderId: string;
  duplicatingStoryId: string | null;
  duplicatedStoryId: string | null;
  /**
   * How many duplicates are still on their way. One skeleton row is shown per
   * pending duplicate, so a bulk run of five reads as five rows arriving rather
   * than as a list that sits still and then jumps.
   */
  pendingDuplicatesCount: number;
  /**
   * The row ids the list held when the duplicate run started.
   *
   * A placeholder stands for a copy that has not arrived yet, so the copies
   * that HAVE arrived are the rows that were not there before. Counting them
   * is what lets a new row and its placeholder swap in one render: the row
   * appears well before the create call returns — `createPage` invalidates the
   * list on success, and the flow then waits on a preview upload — so anything
   * tied to the call finishing left the placeholder up alongside the row it
   * stood in for.
   */
  pendingDuplicatesBaselineIds: string[];
  isFavoritesView: boolean;
  /** Set on a single story's versions page; scopes the list to that story. */
  scopedStoryId: string | null;
  /** Dashboard-only scope pills: all / created by me / recently viewed. */
  homeScope: HomeScope;
  layoutView: "row" | "grid";
  /** Type filter — which kinds of row the list shows. */
  types: DocumentType[];
  /** Location filter — folder ids, each meaning "this folder and below". */
  locationFolderIds: string[];
  dateFilter: DateFilter;
  /** Multi-select. Order is preserved so shift-range anchors stay meaningful. */
  selected: SelectedItem[];
  /** Last row clicked without a modifier — the anchor for shift-range picks. */
  selectionAnchorId: string | null;
  /** Rows waiting to be pasted, and whether pasting copies or moves them. */
  clipboard: ListClipboard | null;
};

type StoriesActions = {
  setType: (type: string) => void;
  setStatus: (status: string) => void;
  setOwner: (owner: string) => void;
  setSearch: (search: string) => void;
  setSortBy: (sortBy: string) => void;
  setSortOrder: (sortOrder: string) => void;
  setFolderId: (folderId: string) => void;
  setDuplicatingStoryId: (id: string | null) => void;
  setDuplicatedStoryId: (id: string | null) => void;
  setPendingDuplicatesCount: (count: number) => void;
  /** Starts a duplicate run: how many copies are owed, and what is on screen. */
  startPendingDuplicates: (count: number, baselineIds: string[]) => void;
  setLayoutView: (layoutView: "row" | "grid") => void;
  setIsFavoritesView: (isFavoritesView: boolean) => void;
  setScopedStoryId: (storyId: string | null) => void;
  setHomeScope: (scope: HomeScope) => void;
  toggleDocumentType: (type: DocumentType) => void;
  setLocationFolderIds: (folderIds: string[]) => void;
  toggleLocationFolderId: (folderId: string) => void;
  setDateFilter: (dateFilter: DateFilter) => void;
  clearDateFilter: () => void;
  selectOnly: (item: SelectedItem) => void;
  toggleSelected: (item: SelectedItem) => void;
  selectRange: (items: SelectedItem[]) => void;
  clearSelection: () => void;
  setClipboard: (clipboard: ListClipboard | null) => void;
  handleInitSetup: () => void;
  reset: () => void;
};

const defaultState = {
  type: "stories",
  status: getStatusFromStorage(),
  owner: getOwnerFromStorage(),
  search: "",
  sortBy: getSortByFromStorage(),
  sortOrder: getSortOrderFromStorage(),
  folderId: getFolderIdFromURL(),
  duplicatingStoryId: null,
  duplicatedStoryId: null,
  pendingDuplicatesCount: 0,
  pendingDuplicatesBaselineIds: [] as string[],
  isFavoritesView: false,
  scopedStoryId: null,
  homeScope: "all" as HomeScope,
  layoutView: getLayoutViewFromStorage(),
  types: getTypesFromStorage(),
  locationFolderIds: getLocationFromStorage(),
  dateFilter: getDateFilterFromStorage(),
  selected: [] as SelectedItem[],
  selectionAnchorId: null,
  clipboard: null as ListClipboard | null,
};

/** Selection is scoped to the visible list, so any navigation drops it. */
const CLEARED_SELECTION = {
  selected: [] as SelectedItem[],
  selectionAnchorId: null,
};

/**
 * The owner filter belongs to the list it was applied to, so going somewhere
 * else — the stories root, a folder, Favorites — drops it. It is persisted, so
 * clearing the state is not enough on its own: a reload would bring the pill
 * back with nothing in the sidebar to explain it.
 */
const clearedOwner = () => {
  saveOwnerToStorage("");
  return { owner: "" };
};

export const useStoriesStore = create<StoriesState & StoriesActions>(
  (set, get) => ({
    ...defaultState,

    setOwner: (owner) => {
      // Picking an owner leaves Favorites, which is the other half of the same
      // rule: the sidebar's two entries under Stories are alternatives, so
      // whichever was clicked last is the one that holds.
      const leavesFavorites = !!owner && get().isFavoritesView;
      set({
        owner,
        ...(leavesFavorites ? { isFavoritesView: false } : {}),
        ...CLEARED_SELECTION,
      });
      saveOwnerToStorage(owner);
    },

    setType: (type) => {
      // The two tabs do not share a status vocabulary — a story is
      // Draft/Live/Archived, a version is Live/Expired/Draft/Accepted — so a
      // status picked on one side would narrow the other by a value it has never
      // heard of, and the server drops unknown values silently.
      const status = get().status.length > 0 ? [] : get().status;
      set({ type, status, ...CLEARED_SELECTION });
      saveStatusToStorage(status);
    },

    setSearch: (search) => {
      const { search: currentSearch, folderId, isFavoritesView } = get();
      // When user starts searching (search goes from empty to non-empty),
      // deselect active folder and disable favorites view
      if (currentSearch.length === 0 && search.length > 0) {
        const updates: Partial<StoriesState> = { search, ...CLEARED_SELECTION };
        // Clear folder selection if active
        if (folderId) {
          updates.folderId = "";
          navigateToFolder("");
        }

        // Disable favorites view if active
        if (isFavoritesView) updates.isFavoritesView = false;
        set(updates);
      } else {
        set({ search, ...CLEARED_SELECTION });
      }
    },

    setIsFavoritesView: (isFavoritesView) =>
      set({
        isFavoritesView,
        folderId: "",
        search: "",
        type: "stories",
        ...(isFavoritesView ? clearedOwner() : {}),
        ...CLEARED_SELECTION,
      }),

    setHomeScope: (homeScope) => set({ homeScope }),

    setScopedStoryId: (scopedStoryId) =>
      set({
        scopedStoryId,
        isFavoritesView: false,
        folderId: "",
        search: "",
        ...CLEARED_SELECTION,
      }),

    setSortBy: (sortBy) => {
      set({ sortBy });
      saveSortByToStorage(sortBy);
    },

    setSortOrder: (sortOrder) => {
      set({ sortOrder });
      saveSortOrderToStorage(sortOrder);
    },

    setFolderId: (folderId) => {
      set({
        folderId,
        isFavoritesView: false,
        search: "",
        ...clearedOwner(),
        ...CLEARED_SELECTION,
      });
      navigateToFolder(folderId);
    },

    setDuplicatingStoryId: (id) => set({ duplicatingStoryId: id }),
    setDuplicatedStoryId: (id) => set({ duplicatedStoryId: id }),
    setPendingDuplicatesCount: (pendingDuplicatesCount) =>
      set({
        pendingDuplicatesCount: Math.max(0, pendingDuplicatesCount),
        // Nothing owed, nothing to measure against.
        ...(pendingDuplicatesCount <= 0
          ? { pendingDuplicatesBaselineIds: [] }
          : {}),
      }),
    startPendingDuplicates: (count, baselineIds) =>
      set({
        pendingDuplicatesCount: Math.max(0, count),
        pendingDuplicatesBaselineIds: baselineIds,
      }),

    setLayoutView: (layoutView) => {
      // The two layouts draw the same documents but not the same rows: the
      // grid keeps folders in their own block, with no card to select. A
      // selection carried across would act on rows the new layout cannot show.
      set({ layoutView, ...CLEARED_SELECTION });
      saveLayoutViewToStorage(layoutView);
    },

    setStatus: (status) => {
      let newStatus: string[];
      if (!status) {
        newStatus = [];
      } else if (get().status.includes(status)) {
        newStatus = get().status.filter((s) => s !== status);
      } else {
        newStatus = [...get().status, status];
      }
      set({ status: newStatus, ...CLEARED_SELECTION });
      saveStatusToStorage(newStatus);
    },

    toggleDocumentType: (type) => {
      const { types } = get();
      const next = types.includes(type)
        ? types.filter((t) => t !== type)
        : [...types, type];
      set({ types: next, ...CLEARED_SELECTION });
      saveTypesToStorage(next);
    },

    setLocationFolderIds: (folderIds) => {
      set({ locationFolderIds: folderIds, ...CLEARED_SELECTION });
      saveLocationToStorage(folderIds);
    },

    toggleLocationFolderId: (folderId) => {
      const { locationFolderIds } = get();
      const next = locationFolderIds.includes(folderId)
        ? locationFolderIds.filter((id) => id !== folderId)
        : [...locationFolderIds, folderId];
      set({ locationFolderIds: next, ...CLEARED_SELECTION });
      saveLocationToStorage(next);
    },

    setDateFilter: (dateFilter) => {
      set({ dateFilter, ...CLEARED_SELECTION });
      saveDateFilterToStorage(dateFilter);
    },

    clearDateFilter: () => {
      set({ dateFilter: DEFAULT_DATE_FILTER, ...CLEARED_SELECTION });
      saveDateFilterToStorage(DEFAULT_DATE_FILTER);
    },

    selectOnly: (item) => set({ selected: [item], selectionAnchorId: item.id }),

    toggleSelected: (item) => {
      const { selected } = get();
      const exists = selected.some((s) => s.id === item.id);
      set({
        selected: exists
          ? selected.filter((s) => s.id !== item.id)
          : [...selected, item],
        // The anchor follows the most recent ctrl-click so a shift-click after
        // it ranges from there, matching how file managers behave.
        selectionAnchorId: item.id,
      });
    },

    // Range picks replace the selection rather than adding to it, so dragging a
    // shift-range smaller shrinks it instead of leaving orphans behind.
    selectRange: (items) =>
      set((state) => ({
        selected: items,
        selectionAnchorId: state.selectionAnchorId,
      })),

    clearSelection: () => set(CLEARED_SELECTION),

    setClipboard: (clipboard) => set({ clipboard }),

    handleInitSetup: () => {
      set({
        folderId: getFolderIdFromURL(),
        status: getStatusFromStorage(),
        sortBy: getSortByFromStorage(),
        sortOrder: getSortOrderFromStorage(),
        layoutView: getLayoutViewFromStorage(),
        types: getTypesFromStorage(),
        owner: getOwnerFromStorage(),
        locationFolderIds: getLocationFromStorage(),
        dateFilter: getDateFilterFromStorage(),
        ...CLEARED_SELECTION,
      });
    },

    // Filters are remembered, so a reset re-reads them from storage rather than
    // dropping the user back on the defaults.
    reset: () =>
      set({
        ...defaultState,
        status: getStatusFromStorage(),
        owner: getOwnerFromStorage(),
        types: getTypesFromStorage(),
        locationFolderIds: getLocationFromStorage(),
        dateFilter: getDateFilterFromStorage(),
      }),
  }),
);

export const useStoriesStoreShallow = <U>(
  selector: (store: StoriesState & StoriesActions) => U,
) => {
  return useStoriesStore(useShallow(selector));
};

/**
 * True when any filter is narrowing the list, i.e. "Clear" is useful.
 *
 * Pass the signed-in user's id and, when the state IS the "Created by me" view,
 * the owner stops counting — there it is the page, not a filter on it — and so
 * does Type, which that view hides and ignores. Without the id both count as
 * before.
 */
export function hasActiveFilters(
  store: StoriesState,
  currentUserId?: string,
): boolean {
  const ownerIsView =
    !!currentUserId &&
    store.owner === currentUserId &&
    !store.folderId &&
    !store.isFavoritesView &&
    !store.scopedStoryId;
  return (
    (!ownerIsView && store.types.length !== DOCUMENT_TYPES.length) ||
    store.locationFolderIds.length > 0 ||
    (store.owner !== "" && !ownerIsView) ||
    store.dateFilter.preset !== null ||
    store.search !== ""
  );
}

// Export store type for use in selectors
export type StoriesStore = StoriesState & StoriesActions;
